import { useState, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteCover, getAlbum, patchAlbum, uploadCover, type AlbumDetail } from "../../../../lib/hostApi";
import { BackIcon, EventShell, HERO_BTN } from "../../../../components/EventPage";
import { EVENT_PHOTO, FORMAT_LABELS, MODE_KEYS } from "../../../../lib/wizard";
import { FILTER_LABELS } from "../../../../lib/theme";
import { msgText, t, useT, type Key, type Msg } from "../../../../lib/i18n";
import { ApiError, type Album } from "../../../../lib/api";
import { resizeCover } from "../../../../lib/image";
import { fmtDate, fmtDateTime } from "../../../../lib/format";
import { uploadClosed } from "../../../../lib/timewindow";

export const Route = createFileRoute("/host/albums/$id/settings")({
  component: SettingsPage,
  validateSearch: (s: Record<string, unknown>): { cover?: "failed" } => (s.cover === "failed" ? { cover: "failed" } : {}),
});

const OUTLINE = "flex flex-1 items-center justify-center rounded-full border border-white/20 px-3 py-2 text-sm font-medium";

/** Pengaturan acara, sementara read-only; hanya sampul dan pesan untuk tamu yang bisa diganti. */
function SettingsPage() {
  const { t } = useT();
  const { id } = Route.useParams();
  const album = useQuery({ queryKey: ["host-album", id], queryFn: () => getAlbum(id) });

  if (album.isPending) return <EventShell>{t("common.loading")}</EventShell>;
  if (album.error || !album.data) return <EventShell>{t("host.album.not_found")}</EventShell>;

  const a = album.data;
  const closed = uploadClosed(a, Date.now() / 1000);
  return (
    <div className="min-h-dvh bg-ink text-cream">
      <main className="mx-auto w-full max-w-md space-y-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="relative h-56 overflow-hidden">
          <img src={a.cover_url || EVENT_PHOTO[a.settings.event_type]} alt="" className="h-full w-full scale-110 object-cover blur-xl" />
          <div aria-hidden className="absolute inset-0 bg-linear-to-b from-transparent from-40% to-ink" />
          <Link to="/host/albums/$id" params={{ id }} aria-label={t("common.back")} className={`${HERO_BTN} absolute left-4 top-4`}>
            <BackIcon />
          </Link>
          <h1 className="absolute bottom-2 left-5 font-serif text-3xl">{t("event.settings.title")}</h1>
        </div>

        <div className="space-y-3 px-5">
          {rows(a).map(([icon, label, value]) => (
            <div key={label} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <Icon d={icon} />
              <span className="shrink-0">{t(label)}</span>
              <span className="min-w-0 flex-1 text-right text-sm text-neutral-400">{value}</span>
            </div>
          ))}

          <CoverCard a={a} id={id} updatedAt={album.dataUpdatedAt} />
          <MessageCard a={a} id={id} />

          {a.status === "pending_payment" && (
            <a href={`/host/albums/${id}/pay`} className="block rounded-2xl border border-amber-400/40 bg-amber-950/30 p-4">
              <p className="font-medium">{t("host.list.status.pending_payment")}</p>
              <p className="text-sm text-neutral-400">{t("host.album.pending_desc")}</p>
              <p className="mt-2 text-sm underline">{t("host.album.pay_now")}</p>
            </a>
          )}
          {closed && <p className="text-sm text-neutral-400">{t("host.album.upload_closed")}</p>}
          <div className="flex gap-2">
            {!closed && (
              <a href={`/host/albums/${id}/pay?kind=topup_photo`} className={OUTLINE}>
                {t("host.album.topup")}
              </a>
            )}
            <a href={`/host/albums/${id}/pay?kind=extend_retention`} className={OUTLINE}>
              {t("host.album.extend")}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

/** [path ikon, label, nilai] per baris pengaturan; dipanggil saat render (ikut bahasa aktif). */
function rows(a: AlbumDetail): [string, Key, ReactNode][] {
  const s = a.settings;
  const camera =
    s.camera === "analogue"
      ? `${t("host.album.settings.camera_analogue")} · ${FILTER_LABELS[a.filter_preset ?? "none"]}`
      : `${t("host.album.settings.camera_instant")} · ${FORMAT_LABELS[s.instant_format!]}`;
  const reveal =
    a.gallery_mode === "delayed"
      ? `${t(MODE_KEYS.delayed)} · ${t("units.minute", { count: a.gallery_delay_min ?? 0 })}`
      : t(MODE_KEYS[a.gallery_mode]);
  return [
    [I.pen, "event.settings.name", a.title ?? t("host.untitled")],
    [I.calendar, "event.settings.start", fmtDateTime(a.event_start)],
    [I.calendar, "event.settings.end", fmtDateTime(a.event_end)],
    [I.clock, "event.settings.reveal", reveal],
    [I.camera, "event.settings.per_guest", a.device_photo_quota],
    [I.aperture, s.camera === "analogue" ? "event.settings.camera_filter" : "event.settings.camera_frame", camera],
    [I.eye, "event.settings.visibility", t(s.visibility === "all" ? "host.wizard.visibility.all_label" : "host.wizard.visibility.own_label")],
    [I.hourglass, "event.settings.expires", a.expires_at ? fmtDate(a.expires_at) : "–"],
  ];
}

const I = {
  pen: "M4 20h4L19 9l-4-4L4 16v4Zm9-13 4 4",
  calendar: "M4 6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6Zm0 4h16M8 3v4m8-4v4",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13v4l3 2",
  camera: "M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Zm8 8.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z",
  aperture: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm2.3-17.7L9 12m6.8-6.5L12 15m5.6-2.3L8.4 9.3m7.5 9.2L12 9m-3.8 8.6L9.7 12M4.5 15h8.7",
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
  hourglass: "M6 3h12M6 21h12M7 3c0 5 10 5 10 9s-10 4-10 9M17 3c0 5-10 5-10 9s10 4 10 9",
};

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d={d} />
  </svg>
);

function CoverCard({ a, id, updatedAt }: { a: Album; id: string; updatedAt: number }) {
  const { t } = useT();
  const qc = useQueryClient();
  // Datang dari wizard dengan cover gagal terunggah (?cover=failed).
  const [err, setErr] = useState<Msg | null>(
    Route.useSearch().cover === "failed" ? { key: "host.album.cover_failed_query" } : null,
  );
  const refresh = () => qc.invalidateQueries({ queryKey: ["host-album", id] });
  const up = useMutation({
    mutationFn: async (file: File) => uploadCover(id, await resizeCover(file)),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? { text: e.message } : { key: "host.album.cover_upload_failed" }),
  });
  const del = useMutation({
    mutationFn: () => deleteCover(id),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? { text: e.message } : { key: "host.album.cover_delete_failed" }),
  });
  const busy = up.isPending || del.isPending;
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="font-medium">{t("host.album.cover_title")}</p>
      {a.cover_url ? (
        <img
          src={`${a.cover_url}?v=${updatedAt}`}
          alt={t("host.album.cover_alt")}
          className="aspect-[3/2] w-full rounded-2xl object-cover"
        />
      ) : (
        <div className="relative">
          <img
            src={EVENT_PHOTO[a.settings.event_type]}
            alt={t("host.album.cover_default_alt")}
            className="aspect-[3/2] w-full rounded-2xl object-cover"
          />
          <span className="absolute left-3 top-3 rounded-full bg-cream px-3 py-1 text-xs font-medium text-cocoa">
            {t("host.album.cover_default_badge")}
          </span>
        </div>
      )}
      <div className="flex gap-2">
        <label
          className={`flex flex-1 cursor-pointer items-center justify-center rounded-full bg-cream px-3 py-2 text-sm font-medium text-ink focus-within:ring-2 focus-within:ring-cream ${busy ? "opacity-50" : ""}`}
        >
          {up.isPending ? t("host.album.uploading") : a.cover_url ? t("host.album.cover_change") : t("host.album.cover_upload")}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setErr(null);
                up.mutate(f);
              }
              e.target.value = "";
            }}
          />
        </label>
        {a.cover_url && (
          <button type="button" disabled={busy} onClick={() => del.mutate()} className={OUTLINE}>
            {t("host.album.cover_remove")}
          </button>
        )}
      </div>
      <p className="text-xs text-neutral-400">{t("host.album.cover_hint")}</p>
      {err && (
        <p className="text-sm text-red-400" role="alert">
          {msgText(t, err)}
        </p>
      )}
    </div>
  );
}


/** Pesan host untuk tamu (layar gabung). */
function MessageCard({ a, id }: { a: Album; id: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [msg, setMsg] = useState(a.host_message ?? "");
  const save = useMutation({
    mutationFn: () => patchAlbum(id, { host_message: msg }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["host-album", id] }),
  });
  const dirty = msg.trim() !== (a.host_message ?? "");
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/5 p-4">
      <label htmlFor="host-message" className="block font-medium">
        {t("host.album.message_title")}
      </label>
      <textarea
        id="host-message"
        rows={3}
        maxLength={280}
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        className="w-full resize-none rounded-2xl border border-white/20 bg-transparent px-3 py-2 text-sm"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className={`rounded-full bg-cream px-4 py-2 text-sm font-medium text-ink ${!dirty || save.isPending ? "opacity-50" : ""}`}
        >
          {t("host.album.message_save")}
        </button>
        {save.isSuccess && !dirty && <span className="text-sm text-neutral-400">{t("host.album.message_saved")}</span>}
      </div>
      <p className="text-xs text-neutral-400">{t("host.album.message_hint")}</p>
      {save.error && (
        <p className="text-sm text-red-400" role="alert">
          {save.error instanceof ApiError ? save.error.message : t("host.album.message_failed")}
        </p>
      )}
    </div>
  );
}
